import { FileObjectType } from "@jsq/ds/core";
import { DocumentStatus } from "../../../graphql/supergraphEnums";
import {
  type RequestedDocumentType,
  type DocumentType,
} from "../../../graphql/supergraphTypes";
import { DocumentFormProps } from "../DocumentCollector/types";
import { getDocumentCategoryInfo } from "../DocumentCollector/utils";

export const mapDocumentsToFormProps = (
  documentTypes: Array<RequestedDocumentType>,
  documents: Array<DocumentType>,
): DocumentFormProps[] => {
  return documentTypes.map((docType) => {
    // Find matching document using either metadata.type or baseType
    // This performs a two-step matching process:
    // 1. First tries to match by document subType if metadata with key='type' exists
    // 2. Falls back to matching by baseType from documentPurpose if no metadata match
    const matchingDocument = documents.find((doc) => {
      if (!doc || !doc.documentPurpose) return false;

      // Step 1: Check if document has metadata with key='type' that matches our subType
      // This allows for more specific document matching beyond just the baseType
      if (docType.subType) {
        const typeMetadata = doc.metadata?.find((meta) => meta.key === "type");
        if (
          typeMetadata &&
          typeMetadata.value &&
          docType.subType === typeMetadata.value &&
          doc.documentPurpose.baseType === docType.baseType
        ) {
          return true;
        }
        return false;
      } else {
        // If no metadata match, fallback to the traditional baseType matching
        // from the documentPurpose object
        return doc.documentPurpose.baseType === docType.baseType;
      }
    });
    const documentCategory = getDocumentCategoryInfo(docType.baseType, docType.subType);

    let file: FileObjectType | null = null;
    const invitedUserEmail = matchingDocument?.invitedUserDetails?.emailAddress;
    if (matchingDocument?.s3Link && !invitedUserEmail) {
      file = {
        status: "accepted",
        file: { name: matchingDocument.name },
        uuid: `${matchingDocument.id}`,
        url: matchingDocument.s3Link,
      } as FileObjectType;
    }

    return {
      id: matchingDocument?.id,
      baseType: docType.baseType,
      subType: docType.subType || null,
      uploaderInfo: invitedUserEmail || null,
      file: file,
      title: documentCategory.title,
      description: documentCategory.description,
      expiryDate: matchingDocument?.expiryDate || null,
      createdAt: matchingDocument?.createdAt || null,
    };
  });
};

export const allDocumentsUploaded = (
  documentTypes: Array<Pick<RequestedDocumentType, "isMandatory" | "status">>,
): boolean => {
  return documentTypes.every(
    (docType) => !docType.isMandatory || docType.status === DocumentStatus.Uploaded,
  );
};
